import objectHash from 'object-hash';

import { get } from './collection-utils';
import { WeakCache } from './memory-cache';

/**
 * Wait for a property to be set on window
 * @param name the window property name set by the script
 */
export function waitForScript<T = any>(name: string): Promise<T> {
  return new Promise((resolve) => {
    if (!name) return resolve(null);

    let windowEntity = get(window, name);
    if (windowEntity) return resolve(windowEntity);

    const interval = setInterval(() => {
      windowEntity = get(window, name);
      if (windowEntity) {
        resolve(windowEntity);
        clearInterval(interval);
      }
    }, 2000);
  });
}

const scriptPromises = new WeakCache<string, Promise<unknown>>();

/**
 * Use this to load async a script and get a promise for the script load
 * @param url the script URL
 * @param id the script id (used to avoid multiple loads)
 * @param name the window property name set by the script (optional)
 */
export function appendScript<T = any>(url: string, id?: string, name?: string): Promise<T> {
  // if is specified a window propery and is already set return it
  if (name && get(window, name)) return Promise.resolve(get(window, name));

  id ||= objectHash(url);
  id = `script-${id}`;

  // if the script is already loaded return (the window property, if asked)
  if (document.getElementById(id)) {
    if (!name) return Promise.resolve(null);

    return waitForScript<T>(name);
  }

  return scriptPromises.getOrSet(
    id,
    () =>
      new Promise<T>((resolve, reject) => {
        const script = document.createElement('script');
        script.type = 'text/javascript';
        script.id = id;
        script.onload = async () => {
          if (!name) return resolve(null);

          const windowProp = await waitForScript<T>(name);
          resolve(windowProp);
        };
        script.onerror = reject;
        script.async = true;
        script.src = url;
        document.head.appendChild(script);
      }),
  ) as Promise<T>;
}

/**
 * Use this to load async a stylesheet and get a promise for the style load
 * @param url the script URL
 * @param id the script id (used to avoid multiple loads)
 */
export function appendStylesheet(url: string, id?: string): Promise<void> {
  id ||= objectHash(url);
  id = `stylesheet-${id}`;

  // if the script is already loaded return (the window property, if asked)
  if (document.getElementById(id)) {
    return;
  }

  return scriptPromises.getOrSet(
    id,
    () =>
      new Promise<void>((resolve, reject) => {
        const stylesheet = document.createElement('link');
        stylesheet.rel = 'stylesheet';
        stylesheet.id = id;
        stylesheet.onload = () => {
          return resolve();
        };
        stylesheet.onerror = reject;
        stylesheet.href = url;
        document.head.appendChild(stylesheet);
      }),
  ) as Promise<void>;
}
